import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PlanCatalogGrid from '../components/PlanCatalogGrid';
import { buildPlanCatalog } from '../utils/planCatalog';
import FeedbackNotification from '../components/FeedbackNotification';
import usePageMetadata from '../utils/usePageMetadata';
import './Billing.css';

const Billing = () => {
  usePageMetadata({
    title: 'Billing & Subscription | The Ad Shop',
    description: 'Manage your subscription plan, billing information, and upgrade your account.'
  });

  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [syncingSubscription, setSyncingSubscription] = useState(false);

  const currentPlanName = (user?.plan || 'free').toLowerCase();
  const isPaidPlan = user?.plan && user.plan !== 'free';
  const hasActiveSubscription = Boolean(user?.stripeSubscriptionId || isPaidPlan);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    if (checkoutStatus === 'success') {
      setMessage('Thanks! Your subscription was updated successfully. Syncing your account now…');
      const syncSubscription = async () => {
        try {
          setSyncingSubscription(true);
          const response = await axios.post('/api/billing/sync-subscription');
          if (response.data?.user) {
            updateUser(response.data.user);
            setMessage('Subscription synced! Redirecting you back to the dashboard…');
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 1500);
          }
        } catch (err) {
          console.error('Failed to sync subscription:', err);
          setError((prev) =>
            prev
              ? prev
              : err.response?.data?.message || 'Subscription updated, but we could not refresh your plan details automatically.'
          );
        } finally {
          setSyncingSubscription(false);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      };
      syncSubscription();
    } else if (params.get('checkout') === 'cancel') {
      setMessage('Checkout was cancelled. No changes were made to your subscription.');
    }
  }, [navigate, updateUser]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoadingPlans(true);
        const response = await axios.get('/api/billing/plans');
        setPlans(response.data?.plans || []);
      } catch (err) {
        console.error('Failed to load plans:', err);
        setPlans([]);
        setError(err.response?.data?.message || 'Unable to load plan information.');
      } finally {
        setLoadingPlans(false);
      }
    };

    loadPlans();
  }, []);

  const planList = useMemo(() => buildPlanCatalog(plans), [plans]);

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      setError('');
      const response = await axios.post('/api/billing/portal-session');
      const { url } = response.data || {};
      if (url) {
        window.location.href = url;
      } else {
        setError('Unable to start billing portal session.');
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      setError(err.response?.data?.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCheckout = async (planName, interval) => {
    const key = `${planName}-${interval}`;
    try {
      setCheckoutLoading(key);
      setError('');
      const response = await axios.post('/api/billing/checkout-session', { planName, interval });
      const { url } = response.data || {};
      if (url) {
        window.location.href = url;
      } else {
        setError('Unable to start checkout session.');
      }
    } catch (err) {
      console.error('Checkout session error:', err);
      setError(err.response?.data?.message || 'Failed to begin checkout.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="billing-page">
      <div className="billing-container">
        <header className="billing-header">
          <h1>Subscription Management</h1>
          <p className="billing-subtitle">
            Compare plans, upgrade when you need more capability, or open the billing portal to manage your invoice and payment details.
          </p>
          {message && <div className="billing-message">{message}</div>}
          {syncingSubscription && !error && (
            <div className="billing-message syncing">Hang tight—updating your plan details…</div>
          )}
          {error && <div className="billing-error">{error}</div>}
        </header>

        {hasActiveSubscription && (
          <div className="billing-actions">
            <button
              className="btn-manage-subscription"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening billing portal…' : 'Open Billing Portal'}
            </button>
            <p className="billing-actions-help">
              Use the billing portal to update payment methods, view invoices, or cancel an existing subscription.
            </p>
          </div>
        )}

        <PlanCatalogGrid
          loading={loadingPlans}
          planList={planList}
          mode="billing"
          currentPlanName={currentPlanName}
          portalLoading={portalLoading}
          checkoutLoading={checkoutLoading}
          onManageSubscription={handleManageSubscription}
          onCheckout={handleCheckout}
        />
      </div>
      <FeedbackNotification />
    </div>
  );
};

export default Billing;

